import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { useFieldErrorMessage } from './useFieldErrorMessage';
import {
  FormFieldCheckbox,
  type FormFieldCheckboxProps
} from '@/components/form/FormFieldCheckbox';

export interface FormCheckboxProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends Omit<
  FormFieldCheckboxProps,
  'name' | 'checked' | 'onCheckedChange' | 'error' | 'defaultValue'
> {
  /**
   * React Hook Form control object
   */
  control: Control<TFieldValues>;

  /**
   * Field name in the form
   */
  name: TName;

  /**
   * Default value for the field
   */
  defaultValue?: boolean;
}

/**
 * React Hook Form adapter for FormFieldCheckbox
 * Automatically handles form state, validation, and errors
 *
 * @example
 * ```tsx
 * const { control } = useForm<FormData>();
 *
 * <FormCheckbox
 *   control={control}
 *   name="agreeToTerms"
 *   label="I agree to the terms and conditions"
 * />
 * ```
 */
export function FormCheckbox<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({ control, name, defaultValue, ...fieldProps }: FormCheckboxProps<TFieldValues, TName>) {
  const fieldErrorMessage = useFieldErrorMessage();

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue as never}
      render={({ field, fieldState }) => (
        <FormFieldCheckbox
          {...fieldProps}
          checked={field.value}
          onCheckedChange={field.onChange}
          error={fieldErrorMessage(fieldState.error)}
        />
      )}
    />
  );
}

FormCheckbox.displayName = 'FormCheckbox';
